import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Users, GraduationCap, FileText, Megaphone, LogOut, KeyRound, Mail, Check, Eye, RefreshCw, Trash2, ShieldCheck, History, MessageCircle, FolderArchive, MessagesSquare, Activity, StickyNote, Video, FileSignature, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { useDocumentViewer } from '../components/DocumentViewer';
import { sendAdminResetLink } from '../lib/passwordResetHelper';
import { friendlyUploadError } from '../lib/errors';
import CollegeManagement from './CollegeManagement';
import StudentManagement from './StudentManagement';
import LeadManagement from './LeadManagement';
import AdminOverview from './AdminOverview';
import StaffManagement from './StaffManagement';
import ActivityLog from './ActivityLog';
import DocumentsArchive from './DocumentsArchive';
import MessageTemplates from './MessageTemplates';
import LeadActivityFeed from './LeadActivityFeed';
import MasterSearch from '../components/MasterSearch';
import PersonalNotes from '../components/PersonalNotes';
import AppointmentsAdmin from '../components/AppointmentsAdmin';
import TeamChatWidget from '../components/TeamChatWidget';
import ProfileChatLite from '../components/ProfileChatLite';
import AssignSelect from '../components/AssignSelect';
import AgreementsLog from '../components/AgreementsLog';
import AgreementComposeDialog from '../components/AgreementComposeDialog';
import { fmtDate } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { key: 'overview', label: 'Overview', Icon: LayoutDashboard },
  { key: 'recruiters', label: 'Recruiters', Icon: Users },
  { key: 'agreements', label: 'Agreements', Icon: FileSignature },
  { key: 'colleges', label: 'Colleges & Commissions', Icon: GraduationCap },
  { key: 'students', label: 'Student Applications', Icon: FileText },
  { key: 'leads', label: 'Lead Management', Icon: Megaphone },
  { key: 'appointments', label: 'Appointments', Icon: Video },
  { key: 'lead_activity', label: 'Sales Activity', Icon: Activity },
  { key: 'templates', label: 'Message Templates', Icon: MessagesSquare },
  { key: 'my_notes', label: 'My Notes', Icon: StickyNote },
  { key: 'documents', label: 'Document Archive', Icon: FolderArchive },
  { key: 'staff', label: 'Staff', Icon: ShieldCheck },
  { key: 'activity', label: 'Activity Log', Icon: History },
];

const TITLES = {
  overview: { title: 'Overview', subtitle: 'Your platform at a glance — leads, partners, applications.' },
  recruiters: { title: 'Recruiters', subtitle: 'Manage recruiter applications and partnerships.' },
  agreements: { title: 'Agreements', subtitle: 'Every recruiter agreement sent and signed via DocuSign.' },
  colleges: { title: 'Colleges & Commissions', subtitle: 'Partner colleges and commission rates.' },
  students: { title: 'Student Applications', subtitle: 'Review, assign and submit student applications.' },
  leads: { title: 'Lead Management', subtitle: 'Meta & Google ad leads pipeline with auto follow-ups.' },
  lead_activity: { title: 'Sales Activity', subtitle: 'Every call, text and note your team logs across every lead.' },
  templates: { title: 'Message Templates', subtitle: 'Edit the SMS, WhatsApp and AI voice script that fire automatically on every new lead.' },
  messages: { title: 'Messages', subtitle: 'Direct conversations with recruiters, students and your team.' },
  my_notes: { title: 'My Notes', subtitle: 'Private notes only you can see and delete.' },
  appointments: { title: 'Appointments', subtitle: 'Video-call bookings from students and recruiters.' },
  documents: { title: 'Document Archive', subtitle: 'Every file uploaded across the platform — recruiters, students and chat.' },
  staff: { title: 'Staff', subtitle: 'Invite teammates and control their module-level access.' },
  activity: { title: 'Activity Log', subtitle: 'Append-only audit trail of every admin and staff action.' },
};

const STATUS_BADGES = {
  pending: 'bg-amber-100 text-amber-800',
  agreement_sent: 'bg-sky-100 text-sky-800',
  agreement_signed: 'bg-violet-100 text-violet-800',
  signed: 'bg-violet-100 text-violet-800', // legacy fallback
  active: 'bg-emerald-100 text-emerald-800',
};

const STATUS_LABELS = {
  pending: 'Pending',
  agreement_sent: 'Agreement Sent',
  agreement_signed: 'Agreement Signed',
  signed: 'Agreement Signed',
  active: 'Active',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [selected, setSelected] = useState(null);
  const [agreeOpen, setAgreeOpen] = useState(false);
  const docViewer = useDocumentViewer();
  const [form, setForm] = useState({
    email_subject: 'Partnership Agreement - True Global Education',
    email_body:
      'Hello,\n\nWelcome aboard as a recruitment partner of True Global Education. Please review and sign the attached partnership agreement at your earliest convenience.\n\nIf you have any questions, simply reply to this email and our partnerships team will get back to you.\n\nWarm regards,\nTrue Global Education',
    agreement_pdf: null,
  });
  const [sending, setSending] = useState(false);
  const [activating, setActivating] = useState(false);
  const [me, setMe] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('tge-sidebar-collapsed') === '1'
  );
  const toggleSidebar = () => {
    setSidebarCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('tge-sidebar-collapsed', next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    // Guard: only admins are allowed in this dashboard. Non-admin users get redirected.
    fetch(`${API}/api/staff/me`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((meData) => {
        if (!meData) { navigate('/admin-login'); return; }
        if (meData.role !== 'admin') { navigate('/staff-dashboard'); return; }
        setMe(meData);
        fetchRecruiters();
      })
      .catch(() => navigate('/admin-login'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll unread count for sidebar badge
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/chat/unread`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (mounted) setUnreadTotal(d.total || 0);
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  async function fetchRecruiters() {
    try {
      const res = await fetch(`${API}/api/admin/recruiters`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 401) {
          setSessionExpired(true);
          return;
        }
        return;
      }
      const d = await res.json();
      setRecruiters(d.recruiters || []);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSendAgreement(e) {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('agreement_pdf', form.agreement_pdf);
      fd.append('email_subject', form.email_subject);
      fd.append('email_body', form.email_body || '');
      const res = await fetch(`${API}/api/admin/recruiters/${selected.recruiter_id}/send-agreement`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setAgreeOpen(false);
      fetchRecruiters();
    } catch (err) {
      alert(friendlyUploadError(err, 'Failed to send agreement.'));
    } finally {
      setSending(false);
    }
  }

  async function handleActivate(rec) {
    if (!window.confirm(`Activate ${rec.company_name}?`)) return;
    setActivating(true);
    try {
      const res = await fetch(`${API}/api/admin/recruiters/${rec.recruiter_id}/activate`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('Activation failed');
      fetchRecruiters();
    } catch (err) {
      alert(err.message);
    } finally {
      setActivating(false);
    }
  }

  const [refreshing, setRefreshing] = useState(false);
  async function handleRefreshStatus(rec) {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/api/admin/recruiters/${rec.recruiter_id}/refresh-status`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Refresh failed');
      await fetchRecruiters();
      if (!d.changed) {
        alert(`DocuSign status: ${d.docusign_status}. Still awaiting signature.`);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDeleteRecruiter(rec) {
    if (!window.confirm(`Permanently delete ${rec.company_name}?\n\nThis will also remove:\n• Their login account\n• All student profiles they submitted\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/recruiters/${rec.recruiter_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Delete failed');
      fetchRecruiters();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleLogout() {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/admin-login');
  }

  if (sessionExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#faf8f3' }}>
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-[0_2px_30px_rgba(27,42,107,0.08)] p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-5">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h2 className="font-serif-display text-3xl text-tge-navy mb-3">Session unavailable</h2>
          <p className="text-tge-muted text-[15px] leading-relaxed">
            Your sign-in worked, but the dashboard cannot reach the API on this domain due to a deployment configuration issue. Your platform admin needs to update the production backend URL.
          </p>
          <div className="bg-tge-cream-soft rounded-xl p-4 mt-6 text-left">
            <div className="text-[11px] uppercase tracking-wider text-tge-gold font-semibold mb-1">What's happening</div>
            <p className="text-xs text-tge-muted leading-relaxed">
              The frontend was built pointing at the preview backend URL. Browsers block authentication cookies across different domains for security. Once Emergent support rebuilds with <code className="bg-white px-1.5 py-0.5 rounded text-tge-navy">REACT_APP_BACKEND_URL=https://trueglobaleducation.com</code>, the dashboard will work.
            </p>
          </div>
          <div className="flex gap-3 mt-7 justify-center">
            <button
              onClick={() => { setSessionExpired(false); fetchRecruiters(); }}
              className="border border-tge-navy/15 text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-tge-cream-soft transition"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/admin-login')}
              className="bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold px-5 py-2.5 rounded-full text-sm transition"
            >
              Back to login
            </button>
          </div>
          <p className="text-xs text-tge-muted mt-6">
            You can still use the preview at{' '}
            <a href="https://student-pathfinder-21.preview.emergentagent.com/admin-login" className="text-tge-gold underline">
              preview.emergentagent.com
            </a>
            {' '}while waiting on the fix.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#faf8f3' }} data-testid="admin-dashboard">
      {/* Sidebar — light, elegant, collapsible + independently scrollable */}
      <aside
        className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-tge-navy/5 flex flex-col fixed inset-y-0 left-0 z-40 transition-[width] duration-200`}
        data-testid="admin-sidebar"
      >
        <div className={`${sidebarCollapsed ? 'px-2' : 'px-6'} py-6 border-b border-tge-navy/5 flex items-center gap-3 relative`}>
          <img
            src="https://customer-assets.emergentagent.com/job_student-pathfinder-21/artifacts/ujq9wzpi_True%20Global%20Logo.png"
            alt="True Global"
            className={`${sidebarCollapsed ? 'h-9 mx-auto' : 'h-11'} w-auto`}
          />
          {!sidebarCollapsed && (
            <div>
              <div className="font-serif-display text-base text-tge-navy leading-none">True Global</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-tge-gold mt-1 font-semibold">Admin Console</div>
            </div>
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="admin-sidebar-toggle"
            className="absolute -right-3 top-8 w-6 h-6 rounded-full bg-white border border-tge-navy/15 shadow-sm hover:border-tge-navy/40 flex items-center justify-center text-tge-navy transition"
          >
            {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1">
          {NAV.map((it) => {
            const isActive = active === it.key;
            return (
              <button
                key={it.key}
                onClick={() => setActive(it.key)}
                data-testid={`admin-nav-${it.key}`}
                title={sidebarCollapsed ? it.label : undefined}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-tge-navy text-white shadow-sm'
                    : 'text-tge-navy/80 hover:bg-tge-cream-soft hover:text-tge-navy'
                }`}
              >
                <it.Icon size={18} strokeWidth={1.7} />
                {!sidebarCollapsed && <span>{it.label}</span>}
                {!sidebarCollapsed && isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-tge-gold" />}
              </button>
            );
          })}
        </nav>

        <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} py-4 border-t border-tge-navy/5 space-y-1`}>
          <button
            onClick={() => setPasswordOpen(true)}
            data-testid="admin-change-password-btn"
            title={sidebarCollapsed ? 'Change password' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium text-tge-navy/70 hover:bg-tge-cream-soft hover:text-tge-navy transition`}
          >
            <KeyRound size={18} />
            {!sidebarCollapsed && <span>Change password</span>}
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            title={sidebarCollapsed ? 'Logout' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition`}
          >
            <LogOut size={18} />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
          {!sidebarCollapsed && (
            <div className="mt-4 pt-4 border-t border-tge-navy/5 px-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-tge-navy to-blue-700 text-white flex items-center justify-center font-serif-display text-sm">
                  I
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-tge-navy truncate">info@trueglobal...</div>
                  <div className="text-[10px] text-tge-muted">Admin</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />

      {/* Main */}
      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} flex flex-col transition-[margin] duration-200`}>
        <header className="bg-white/80 backdrop-blur-md border-b border-tge-navy/5 px-8 py-5 sticky top-0 z-30">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-between"
          >
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-tge-gold font-semibold">Admin</div>
              <h1 className="font-serif-display text-2xl text-tge-navy mt-0.5">{TITLES[active]?.title}</h1>
              <p className="text-sm text-tge-muted">{TITLES[active]?.subtitle}</p>
            </div>
            <div className="flex items-center gap-4 flex-1 justify-end ml-6">
              <MasterSearch onNavigate={(tab) => setActive(tab)} />
              <div className="hidden lg:flex items-center gap-2 text-xs text-tge-muted shrink-0">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>All systems normal</span>
              </div>
            </div>
          </motion.div>
        </header>

        <main className="flex-1 px-8 py-7">
          {error && (
            <div className="mb-6 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {active === 'overview' && <AdminOverview />}
          {active === 'recruiters' && (
            <RecruitersTab
              recruiters={recruiters}
              currentUserId={me?.id}
              activating={activating}
              onActivate={handleActivate}
              refreshing={refreshing}
              onRefreshStatus={handleRefreshStatus}
              onRefreshAll={fetchRecruiters}
              onDelete={handleDeleteRecruiter}
              selected={selected}
              setSelected={setSelected}
              agreeOpen={agreeOpen}
              setAgreeOpen={setAgreeOpen}
              form={form}
              setForm={setForm}
              sending={sending}
              onSendAgreement={handleSendAgreement}
              onViewDoc={(r, key, label) => docViewer.open({
                url: `${API}/api/admin/recruiters/${r.recruiter_id}/documents/${key}`,
                label,
                ownerName: r.company_name,
              })}
            />
          )}
          {active === 'agreements' && <AgreementsLog />}
          {active === 'colleges' && <CollegeManagement />}
          {active === 'students' && <StudentManagement />}
          {active === 'leads' && <LeadManagement />}
          {active === 'lead_activity' && <LeadActivityFeed />}
          {active === 'templates' && <MessageTemplates />}
          {active === 'my_notes' && <PersonalNotes />}
          {active === 'appointments' && <AppointmentsAdmin />}
          {active === 'documents' && <DocumentsArchive />}
          {active === 'staff' && <StaffManagement />}
          {active === 'activity' && <ActivityLog />}
        </main>
      </div>
      {me && <TeamChatWidget currentUserId={me.id} />}
      {docViewer.element}
    </div>
  );
}

function RecruitersTab({ recruiters, currentUserId, activating, onActivate, refreshing, onRefreshStatus, onRefreshAll, onDelete, selected, setSelected, agreeOpen, setAgreeOpen, form, setForm, sending, onSendAgreement, onViewDoc }) {
  const [chatRecruiter, setChatRecruiter] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', company_name: '' });
  const [createBusy, setCreateBusy] = useState(false);
  const [createBusinessLicense, setCreateBusinessLicense] = useState(null);
  const [createIdProof, setCreateIdProof] = useState(null);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (createBusy) return;
    setCreateBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', createForm.name);
      fd.append('email', createForm.email);
      fd.append('company_name', createForm.company_name);
      if (createBusinessLicense) fd.append('business_license', createBusinessLicense);
      if (createIdProof) fd.append('id_proof', createIdProof);
      const res = await fetch(`${API}/api/admin/recruiters`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', company_name: '' });
      setCreateBusinessLicense(null);
      setCreateIdProof(null);
      onRefreshAll();
      alert(`Recruiter created (${data.recruiter_id}). You can now send them the agreement.`);
    } catch (err) {
      alert(friendlyUploadError(err, 'Failed to create recruiter.'));
    } finally {
      setCreateBusy(false);
    }
  };

  const counts = {
    total: recruiters.length,
    pending: recruiters.filter((r) => r.status === 'pending').length,
    sent: recruiters.filter((r) => r.status === 'agreement_sent').length,
    signed: recruiters.filter((r) => r.status === 'agreement_signed' || r.status === 'signed').length,
    active: recruiters.filter((r) => r.status === 'active').length,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { l: 'Total', v: counts.total, c: 'text-tge-navy' },
          { l: 'Pending', v: counts.pending, c: 'text-amber-600' },
          { l: 'Agreement Sent', v: counts.sent, c: 'text-sky-600' },
          { l: 'Signed', v: counts.signed, c: 'text-violet-600' },
          { l: 'Active', v: counts.active, c: 'text-emerald-600' },
        ].map((s, i) => (
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl p-5 border border-tge-navy/5"
          >
            <div className="text-[11px] uppercase tracking-wider text-tge-muted">{s.l}</div>
            <div className={`font-serif-display text-3xl mt-2 ${s.c}`}>{s.v}</div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
        <div className="px-6 py-5 border-b border-tge-navy/5 flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-tge-navy">Recruiter applications</h3>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="bg-tge-gold hover:bg-tge-gold-soft text-tge-navy font-semibold rounded-full text-xs h-8"
              data-testid="admin-new-recruiter-btn"
            >
              + New Recruiter
            </Button>
            <button
              onClick={onRefreshAll}
              className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft"
              data-testid="recruiters-refresh-all"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>
        {recruiters.length === 0 ? (
          <div className="text-center py-16 text-tge-muted">
            <p>No recruiters yet. Share the recruiter signup link to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-tge-cream-soft">
                <tr>
                  {['Recruiter', 'Company', 'Status', 'Documents', 'Assigned', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold text-tge-muted uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-tge-cream">
                {recruiters.map((r) => (
                  <tr key={r.recruiter_id} className="hover:bg-tge-cream-soft/50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-tge-gold/20 text-tge-gold font-serif-display flex items-center justify-center">
                          {r.company_name?.charAt(0) || 'R'}
                        </div>
                        <div>
                          <div className="font-medium text-tge-navy text-sm">{r.recruiter_id}</div>
                          <div className="text-xs text-tge-muted">{fmtDate(r.created_at)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy font-medium">{r.company_name}</td>
                    <td className="px-6 py-4">
                      <Badge className={`${STATUS_BADGES[r.status] || 'bg-gray-100 text-gray-800'} border-0`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1.5"
                          onClick={() => onViewDoc(r, 'business_license', 'Business License')}
                          data-testid={`recruiter-doc-license-${r.recruiter_id}`}
                          disabled={!r.business_license_url}
                          title={r.business_license_url ? 'View business license' : 'Not uploaded'}
                        >
                          <Eye size={12} /> License
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1.5"
                          onClick={() => onViewDoc(r, 'id_proof', 'ID Proof')}
                          data-testid={`recruiter-doc-idproof-${r.recruiter_id}`}
                          disabled={!r.id_proof_url}
                          title={r.id_proof_url ? 'View ID proof' : 'Not uploaded'}
                        >
                          <Eye size={12} /> ID Proof
                        </Button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <AssignSelect
                        resourceType="recruiter"
                        resourceId={r.recruiter_id}
                        currentAssigneeId={r.assigned_to}
                        onAssigned={onRefreshAll}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1.5"
                          onClick={() => setChatRecruiter(r)}
                          data-testid={`recruiter-chat-btn-${r.recruiter_id}`}
                        >
                          <MessageCircle size={12} /> Chat
                        </Button>
                      <Dialog
                        open={agreeOpen && selected?.recruiter_id === r.recruiter_id}
                        onOpenChange={(o) => { setAgreeOpen(o); if (o) setSelected(r); }}
                      >
                        <DialogTrigger asChild>
                          {r.status === 'pending' ? (
                            <Button size="sm" className="bg-tge-navy hover:bg-tge-navy-deep text-white" data-testid={`send-agreement-btn-${r.recruiter_id}`}>
                              Send Agreement
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="text-xs border-tge-gold/50 text-tge-navy hover:bg-tge-cream-soft" data-testid={`send-agreement-btn-${r.recruiter_id}`}>
                              Resend Agreement
                            </Button>
                          )}
                        </DialogTrigger>
                        <AgreementComposeDialog
                          recruiter={r}
                          form={form}
                          setForm={setForm}
                          sending={sending}
                          onSubmit={onSendAgreement}
                          onClose={() => setAgreeOpen(false)}
                        />
                      </Dialog>
                      {r.status === 'agreement_sent' && (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2 text-xs text-sky-700">
                            <Mail size={14} />
                            <span>Awaiting signature{r.agreement_sent_at ? ` · sent ${fmtDate(r.agreement_sent_at)}` : ''}</span>
                          </div>
                          <button
                            onClick={() => onRefreshStatus(r)}
                            disabled={refreshing}
                            className="inline-flex items-center gap-1 text-[11px] text-tge-navy hover:text-tge-gold transition px-2 py-1 rounded-full border border-tge-navy/10 hover:border-tge-gold/50 disabled:opacity-50"
                            data-testid={`refresh-status-btn-${r.recruiter_id}`}
                            title="Check DocuSign for latest signature status"
                          >
                            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> Check
                          </button>
                        </div>
                      )}
                      {(r.status === 'agreement_signed' || r.status === 'signed') && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-tge-navy/20 text-tge-navy hover:bg-tge-cream-soft"
                            onClick={() => window.open(`${API}/api/admin/recruiters/${r.recruiter_id}/agreement`, '_blank')}
                            data-testid={`view-agreement-btn-${r.recruiter_id}`}
                          >
                            <Eye size={13} className="mr-1" /> View Agreement
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            onClick={() => onActivate(r)}
                            disabled={activating}
                            data-testid={`activate-btn-${r.recruiter_id}`}
                          >
                            Activate
                          </Button>
                        </div>
                      )}
                      {r.status === 'active' && (
                        <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                          <Check size={13} /> Activated {r.activated_at && `· ${fmtDate(r.activated_at)}`}
                        </span>
                      )}
                      {r.status === 'active' && r.email && (
                        <button
                          onClick={() => sendAdminResetLink({ email: r.email, displayName: r.company_name || r.email })}
                          className="ml-2 inline-flex items-center gap-1 text-[11px] text-tge-navy hover:text-tge-gold transition px-2 py-1 rounded-full border border-tge-navy/10 hover:border-tge-gold/50"
                          title="Email a password-reset link"
                          data-testid={`recruiter-reset-${r.recruiter_id}`}
                        >
                          <KeyRound size={11} /> Reset password
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(r)}
                        className="ml-3 text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded-md transition inline-flex items-center"
                        title="Delete recruiter"
                        data-testid={`delete-recruiter-btn-${r.recruiter_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chat with recruiter — lives inside the recruiter profile popup */}
      <Dialog open={!!chatRecruiter} onOpenChange={(o) => !o && setChatRecruiter(null)}>
        <DialogContent className="max-w-lg w-[94vw]" data-testid="recruiter-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-tge-navy text-xl">
              {chatRecruiter?.company_name}
            </DialogTitle>
            <DialogDescription>
              {chatRecruiter?.contact_name ? `${chatRecruiter.contact_name} · ` : ''}{chatRecruiter?.contact_email || chatRecruiter?.recruiter_id} — chat directly with this recruiter. All messages are saved here.
            </DialogDescription>
          </DialogHeader>
          {chatRecruiter && (
            <ProfileChatLite targetUserId={chatRecruiter.user_id} currentUserId={currentUserId} heightClass="h-80" />
          )}
        </DialogContent>
      </Dialog>

      {/* Create new recruiter dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md w-[92vw]" data-testid="admin-create-recruiter-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-tge-navy text-xl">Create recruiter</DialogTitle>
            <DialogDescription>
              Add a recruiter directly. Documents are optional — you can attach them later. The recruiter will get a setup email when you click Activate or Send Agreement.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Contact name *</Label>
              <Input
                required
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Full name"
                data-testid="admin-create-recruiter-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Contact email *</Label>
              <Input
                required
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="contact@company.com"
                data-testid="admin-create-recruiter-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Company / agency name *</Label>
              <Input
                required
                value={createForm.company_name}
                onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })}
                placeholder="Acme Education"
                data-testid="admin-create-recruiter-company"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Business license (optional, PDF/image)</Label>
              <Input type="file" accept=".pdf,image/*" onChange={(e) => setCreateBusinessLicense(e.target.files?.[0] || null)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">ID proof (optional, PDF/image)</Label>
              <Input type="file" accept=".pdf,image/*" onChange={(e) => setCreateIdProof(e.target.files?.[0] || null)} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createBusy} className="flex-1 bg-tge-navy hover:bg-tge-navy-deep text-white" data-testid="admin-create-recruiter-submit">
                {createBusy ? 'Creating...' : 'Create recruiter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
